/**
 * Location Filter Component
 * Search and select user location for filtering restaurants and offers
 * Integrates with Google Places API for autocomplete functionality
 */

import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  inject,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  LocationService,
  Location,
  LocationPrediction,
} from '../../../core/services/location.service';

@Component({
  selector: 'app-location-filter',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './location-filter.component.html',
  styleUrls: ['./location-filter.component.scss'],
})
export class LocationFilterComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  private locationService = inject(LocationService);
  private translate = inject(TranslateService);

  // Signals for reactive state
  isOpen = signal(false);
  searchQuery = signal('');
  currentLocation = this.locationService.currentLocation;
  predictions = signal<LocationPrediction[]>([]);
  isLoading = signal(false);
  isGettingGeolocation = signal(false); // Separate flag for geolocation loading
  error = signal<string | null>(null);

  // Debounce subject for search input
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Computed signal for filtered locations (using predictions)
  filteredLocations = computed(() => {
    // Return the actual predictions wrapped in objects for display
    return this.predictions();
  });

  // Display text for the selected location
  displayText = computed(() => {
    const location = this.currentLocation();
    if (!location) {
      return '';
    }

    let text = location.name;

    // Add city if available and different from name
    if (location.city && location.city !== location.name) {
      text += `, ${location.city}`;
    }

    // Add postal code if available
    if (location.postalCode) {
      text += ` (${location.postalCode})`;
    }

    return text;
  });

  private clickListener?: (event: MouseEvent) => void;

  ngOnInit(): void {
    // Setup debounced search listener
    this.searchSubject
      .pipe(
        debounceTime(300), // Wait 300ms after user stops typing
        distinctUntilChanged(), // Only process if input changed
        takeUntil(this.destroy$),
      )
      .subscribe((searchTerm) => {
        this.performSearch(searchTerm);
      });

    // Subscribe to prompt location events (e.g., from places page when location is required)
    this.locationService.promptLocation$
      .pipe(takeUntil(this.destroy$))
      .subscribe((shouldPrompt) => {
        if (shouldPrompt) {
          console.log('📍 Opening location picker from prompt');
          this.isOpen.set(true);
          setTimeout(() => {
            this.searchInput?.nativeElement.focus();
          }, 100);
        }
      });

    // Close dropdown when clicking outside
    this.clickListener = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.location-filter-container')) {
        this.isOpen.set(false);
      }
    };
    document.addEventListener('click', this.clickListener);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.clickListener) {
      document.removeEventListener('click', this.clickListener);
    }
  }

  /**
   * Toggle dropdown
   */
  toggleDropdown(): void {
    this.isOpen.update((open) => !open);
    if (this.isOpen()) {
      setTimeout(() => {
        this.searchInput?.nativeElement.focus();
      }, 100);
    }
  }

  /**
   * Handle search input with debouncing
   */
  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
    this.error.set(null);

    if (input.value.length < 2) {
      this.predictions.set([]);
      return;
    }

    // Trigger debounced search
    this.searchSubject.next(input.value);
  }

  /**
   * Perform the actual API call to get predictions
   */
  private performSearch(searchTerm: string): void {
    this.isLoading.set(true);
    this.predictions.set([]);

    this.locationService
      .getAutocompletePredictions(searchTerm)
      .then((predictions) => {
        this.predictions.set(predictions);
        this.isLoading.set(false);
      })
      .catch((error) => {
        console.error('Error fetching predictions:', error);
        this.error.set(this.translate.instant('location.locationError'));
        this.isLoading.set(false);
      });
  }

  /**
   * Select a location (convert prediction to location asynchronously)
   * Guards against multiple concurrent requests
   */
  async selectLocation(prediction: LocationPrediction): Promise<void> {
    // Prevent multiple concurrent location selection requests
    if (this.isLoading()) {
      console.log('⏳ Location selection already in progress, ignoring click');
      return;
    }

    // It's a prediction, convert it to a Location
    try {
      this.isLoading.set(true);
      const location =
        await this.locationService.convertPlaceToLocation(prediction);
      // Skip auto-zoom when manually selecting location from picker
      this.locationService.setSkipZoomOnNextChange();
      this.locationService.setLocation(location);
      this.isOpen.set(false);
      this.searchQuery.set('');
      this.predictions.set([]);
    } catch (error) {
      console.error('Error converting place to location:', error);
      this.error.set(this.translate.instant('location.locationError'));
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Clear selected location
   */
  clearLocation(event: Event): void {
    event.stopPropagation();
    this.locationService.clearLocation();
  }

  /**
   * Handle keyboard navigation
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.isOpen.set(false);
    }
  }

  /**
   * Use current geolocation
   * Guards against multiple concurrent requests
   */
  async useCurrentLocation(): Promise<void> {
    // Prevent multiple concurrent geolocation requests
    if (this.isGettingGeolocation()) {
      console.log(
        '📍 [Filter] Geolocation request already in progress, ignoring click',
      );
      return;
    }

    try {
      console.log('📍 [Filter] Starting geolocation request...');
      this.isGettingGeolocation.set(true);
      this.error.set(null);
      await this.locationService.useCurrentGeolocation();
      console.log('📍 [Filter] Geolocation successful, closing dropdown');
      this.isOpen.set(false);
      this.searchQuery.set('');
      this.predictions.set([]);
    } catch (error: any) {
      console.error('📍 [Filter] Geolocation failed:', error);
      this.error.set(error.message || this.translate.instant('location.couldNotGetLocation'));
    } finally {
      this.isGettingGeolocation.set(false);
    }
  }
}
